/**
 * Remove from Wishlist Action
 * Triggered by: arc:action:wishlistRemove
 */

import { readStoredJson } from "@js/utils/storage";

// Global wishlist state (shared with add action)
// Read at import, so it must never throw: see src/js/utils/storage.js.
let wishlistProducts = readStoredJson('wishlist_products', []);

export const runWishlistRemove = async (data = {}) => {
    try {
        const { 
            productId,
            variantId,
            showNotification = true,
            showConfirmation = false
        } = data;

        if (!productId && !variantId) {
            throw new Error('Product ID or variant ID is required');
        }

        const finalProductId = productId || variantId;

        // Check if item exists in wishlist
        const productIndex = wishlistProducts.findIndex(product => 
            parseInt(product.id) === parseInt(finalProductId)
        );

        if (productIndex === -1) {
            window.dispatchEvent(new CustomEvent('wishlist:notFound', {
                detail: {
                    productId: finalProductId,
                    message: 'Product not found in wishlist'
                }
            }));

            return {
                success: false,
                notFound: true,
                productId: finalProductId,
                message: 'Product not found in wishlist'
            };
        }

        // Show confirmation if required
        if (showConfirmation) {
            const confirmed = await showRemoveConfirmation(finalProductId);
            if (!confirmed) {
                return {
                    success: false,
                    cancelled: true,
                    message: 'Removal cancelled'
                };
            }
        }

        // Get product data before removal
        const productToRemove = wishlistProducts[productIndex];

        // Dispatch remove attempt event
        window.dispatchEvent(new CustomEvent('wishlist:removeAttempt', {
            detail: {
                productId: finalProductId,
                product: productToRemove,
                showNotification
            }
        }));

        // Remove from wishlist
        wishlistProducts = wishlistProducts.filter(product => 
            parseInt(product.id) !== parseInt(finalProductId)
        );

        // Save to localStorage
        localStorage.setItem('wishlist_products', JSON.stringify(wishlistProducts));

        // Dispatch success event
        window.dispatchEvent(new CustomEvent('wishlist:removeSuccess', {
            detail: {
                product: productToRemove,
                productId: finalProductId,
                totalItems: wishlistProducts.length,
                wishlist: wishlistProducts,
                timestamp: Date.now()
            }
        }));

        // Dispatch wishlist updated event
        window.dispatchEvent(new CustomEvent('wishlist:updated', {
            detail: {
                action: 'remove',
                product: productToRemove,
                wishlist: wishlistProducts,
                totalItems: wishlistProducts.length
            }
        }));

        if (showNotification) {
            window.dispatchEvent(new CustomEvent('notification:show', {
                detail: {
                    type: 'info',
                    message: 'Removed from wishlist',
                    product: productToRemove
                }
            }));
        }

        return {
            success: true,
            product: productToRemove,
            productId: finalProductId,
            totalItems: wishlistProducts.length,
            wishlist: wishlistProducts,
            message: 'Product removed from wishlist successfully'
        };

    } catch (error) {
        console.error('Wishlist Remove error:', error);
        
        window.dispatchEvent(new CustomEvent('wishlist:removeError', {
            detail: {
                error: error.message,
                productId: data.productId || data.variantId
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

// Show remove confirmation dialog
const showRemoveConfirmation = async (productId) => {
    return new Promise((resolve) => {
        // Dispatch confirmation event - theme can handle this with a modal
        window.dispatchEvent(new CustomEvent('wishlist:removeConfirm', {
            detail: {
                productId,
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false)
            }
        }));

        // Fallback to browser confirm if no custom handler
        setTimeout(() => {
            const confirmed = confirm('Are you sure you want to remove this item from your wishlist?');
            resolve(confirmed);
        }, 100);
    });
};

// Clear entire wishlist
export const clearWishlist = async (showConfirmation = true) => {
    try {
        if (showConfirmation) {
            const confirmed = await showClearConfirmation();
            if (!confirmed) {
                return {
                    success: false,
                    cancelled: true,
                    message: 'Clear cancelled'
                };
            }
        }

        const previousWishlist = [...wishlistProducts];
        wishlistProducts = [];
        
        // Save to localStorage
        localStorage.setItem('wishlist_products', JSON.stringify(wishlistProducts));

        // Dispatch clear event
        window.dispatchEvent(new CustomEvent('wishlist:cleared', {
            detail: {
                previousWishlist,
                timestamp: Date.now()
            }
        }));

        // Dispatch updated event
        window.dispatchEvent(new CustomEvent('wishlist:updated', {
            detail: {
                action: 'clear',
                wishlist: wishlistProducts,
                totalItems: 0
            }
        }));

        return {
            success: true,
            message: 'Wishlist cleared successfully'
        };

    } catch (error) {
        return {
            success: false,
            error: error.message
        };
    }
};

// Show clear confirmation dialog
const showClearConfirmation = async () => {
    return new Promise((resolve) => {
        window.dispatchEvent(new CustomEvent('wishlist:clearConfirm', {
            detail: {
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false)
            }
        }));

        setTimeout(() => {
            const confirmed = confirm('Are you sure you want to clear your entire wishlist?');
            resolve(confirmed);
        }, 100);
    });
};

// Initialize wishlist remove functionality
export const initializeWishlistRemove = (config = {}) => {
    const {
        removeButtonSelector = '[data-wishlist-remove]',
        clearButtonSelector = '[data-wishlist-clear]',
        autoSetupButtons = true
    } = config;

    if (autoSetupButtons) {
        setupWishlistRemoveButtons(removeButtonSelector);
        setupWishlistClearButtons(clearButtonSelector);
    }

    window.dispatchEvent(new CustomEvent('wishlistRemove:initialized', {
        detail: { config, timestamp: Date.now() }
    }));
};

// Setup remove button listeners
const setupWishlistRemoveButtons = (buttonSelector) => {
    const removeButtons = document.querySelectorAll(buttonSelector);
    
    removeButtons.forEach(button => {
        button.removeEventListener('click', handleWishlistRemoveClick);
        button.addEventListener('click', handleWishlistRemoveClick);
    });
};

// Setup clear button listeners
const setupWishlistClearButtons = (buttonSelector) => {
    const clearButtons = document.querySelectorAll(buttonSelector);
    
    clearButtons.forEach(button => {
        button.removeEventListener('click', handleWishlistClearClick);
        button.addEventListener('click', handleWishlistClearClick);
    });
};

// Handle remove button clicks
const handleWishlistRemoveClick = async (event) => {
    event.preventDefault();
    
    const button = event.target.closest('[data-wishlist-remove]');
    if (!button) return;
    
    const productId = button.dataset.productId || button.dataset.wishlistRemove;
    const variantId = button.dataset.variantId;
    const showNotification = button.dataset.showNotification !== 'false';
    const showConfirmation = button.dataset.showConfirmation === 'true';

    if (!productId && !variantId) {
        console.error('Product ID or variant ID not found for remove button');
        return;
    }

    // Update button state
    button.classList.add('removing');
    const originalText = button.textContent;
    const loadingText = button.dataset.loadingText || 'Removing...';
    button.textContent = loadingText;

    try {
        const result = await window.dispatchEvent(new CustomEvent('arc:action:wishlistRemove', {
            detail: {
                productId,
                variantId,
                showNotification,
                showConfirmation
            }
        }));

        // Remove the item element from DOM if successful
        if (result?.success !== false) {
            removeWishlistItemFromDOM(button, productId || variantId);
        }

    } finally {
        // Reset button state if still in DOM
        if (button.isConnected) {
            button.classList.remove('removing');
            button.textContent = originalText;
        }
    }
};

// Handle clear button clicks
const handleWishlistClearClick = async (event) => {
    event.preventDefault();
    
    const button = event.target;
    const showConfirmation = button.dataset.showConfirmation !== 'false';

    // Update button state
    button.disabled = true;
    const originalText = button.textContent;
    button.textContent = 'Clearing...';

    try {
        const result = await clearWishlist(showConfirmation);
        
        if (result.success) {
            // Clear all wishlist items from DOM
            clearWishlistItemsFromDOM();
        }

    } finally {
        // Reset button state
        button.disabled = false;
        button.textContent = originalText;
    }
};

// Remove wishlist item from DOM
const removeWishlistItemFromDOM = (button, productId) => {
    const itemElement = button.closest('[data-wishlist-item]') || 
                       button.closest(`[data-product-id="${productId}"]`) ||
                       button.closest('.wishlist-item');
    
    if (itemElement) {
        // Add fade out animation
        itemElement.style.transition = 'opacity 0.3s ease';
        itemElement.style.opacity = '0';
        
        setTimeout(() => {
            itemElement.remove();
            
            // Check if wishlist is now empty and show empty state
            const remainingItems = document.querySelectorAll('[data-wishlist-item]');
            if (remainingItems.length === 0) {
                showEmptyWishlistState();
            }
        }, 300);
    }
};

// Clear all wishlist items from DOM
const clearWishlistItemsFromDOM = () => {
    const wishlistItems = document.querySelectorAll('[data-wishlist-item]');
    
    wishlistItems.forEach((item, index) => {
        item.style.transition = 'opacity 0.3s ease';
        item.style.opacity = '0';
        
        setTimeout(() => {
            item.remove();
            
            // Show empty state after last item is removed
            if (index === wishlistItems.length - 1) {
                showEmptyWishlistState();
            }
        }, 300 + (index * 50)); // Stagger the removal animation
    });
};

// Show empty wishlist state
const showEmptyWishlistState = () => {
    const emptyState = document.querySelector('[data-wishlist-empty]');
    if (emptyState) {
        emptyState.style.display = 'block';
    }
    
    // Dispatch empty state event
    window.dispatchEvent(new CustomEvent('wishlist:empty', {
        detail: { timestamp: Date.now() }
    }));
};

// Move wishlist item to cart
export const moveWishlistItemToCart = async (productId, quantity = 1) => {
    try {
        // First add to cart
        const cartResult = await window.dispatchEvent(new CustomEvent('arc:action:addToCart', {
            detail: {
                variantId: productId,
                quantity: quantity
            }
        }));

        if (cartResult?.success !== false) {
            // Then remove from wishlist
            const removeResult = await window.dispatchEvent(new CustomEvent('arc:action:wishlistRemove', {
                detail: {
                    productId: productId,
                    showNotification: false
                }
            }));

            if (removeResult?.success !== false) {
                window.dispatchEvent(new CustomEvent('notification:show', {
                    detail: {
                        type: 'success',
                        message: 'Moved to cart successfully'
                    }
                }));

                return {
                    success: true,
                    message: 'Item moved to cart successfully'
                };
            }
        }

        throw new Error('Failed to move item to cart');

    } catch (error) {
        window.dispatchEvent(new CustomEvent('wishlist:moveToCartError', {
            detail: {
                error: error.message,
                productId
            }
        }));

        return {
            success: false,
            error: error.message
        };
    }
};

export default runWishlistRemove;
